import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  exportHHAnnualReport, exportHHMonthlyReport, exportHHQuarterlyReport,
  getHHAnnualReport, getHHMonthlyReport, getHHQuarterlyReport, setHHActivation,
} from '../../api/client'
import { currentLocale } from '../../i18n'
import { useCostLock } from './costLock'
import CostUnlock from './CostUnlock'
import WeekDetailModal from './WeekDetailModal'
import VisibilityMenu from './VisibilityMenu'
import {
  byExplicitOrder, crossFilter, money, num, projectAlias, projectCode, projectTitle,
  quarterOf, reordered,
} from './sheet'

/**
 * "Balance mensual": the history of hours spent on the company's projects,
 * engineer by engineer -- one column per project, one row per person, the
 * shape of the sheet the PMO already keeps by hand.
 *
 * The hours here are the ones weekly planning actually recorded (Plan Week's
 * live rows and the frozen snapshots of closed weeks). Project STAFFING is
 * deliberately not part of it: dotacion says who WILL work, and this tab is
 * about what was worked -- see `availability.booked_hours(include_committed)`,
 * where that line is drawn once for both readers of it.
 *
 * The trailing columns are derived, not sources: Total HH, Disponible (the
 * month's business days at the person's own weekly hours) and Diferencia
 * between them, plus cost when the reader unlocks it. The last row totals
 * each project's hours and their cost. Month, quarter and year all render
 * through this one path because `hh_reports.build_span_report` hands back the
 * same row shape a single month does -- a column can never quietly mean
 * something different depending on the period selected.
 *
 * The two menus pick which projects and people are in play; the table then
 * drops any row or column that is empty within that selection, in both
 * directions. Pick three projects and only the people who charged hours to
 * them are listed; pick three people and only the projects they worked on
 * are. With nothing picked either way that rule already yields the default
 * everyone wants -- whatever has hours in the period -- so there is no
 * separate default to configure or reset to.
 *
 * A person's Total HH, Disponible and Diferencia stay their real figures
 * across every project they have, shown or not: those three are a statement
 * about the person's month, and recomputing them per selection would turn
 * "Diferencia" into a different number depending on which columns happened
 * to be on screen. Only the totals ROW follows the selection.
 *
 * Columns and rows drag into whatever order the reader wants, which is why
 * there is also a full-screen view: reordering a wide sheet needs the room.
 */
export default function MonthlyReport() {
  const { t } = useTranslation(['clevel', 'common'])
  const { password, unlocked, lock } = useCostLock()
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [period, setPeriod] = useState('month')     // month | quarter | year
  const [costBasis, setCostBasis] = useState('company')
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [busyProject, setBusyProject] = useState(null)
  const [asking, setAsking] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  // { mode: 'project'|'person', id, name } or null. Only meaningful in month
  // view -- the weekly drill-down needs a single month to pick weeks from.
  const [drillDown, setDrillDown] = useState(null)
  // Visibility -- a display choice, and the menus below are the only say over
  // it, so checking something there always makes it appear.
  const [hiddenProjects, setHiddenProjects] = useState(() => new Set())
  const [hiddenMembers, setHiddenMembers] = useState(() => new Set())
  // Whatever order the reader dragged things into, as ids. Empty means the
  // order the API sent (most hours first).
  const [projectOrder, setProjectOrder] = useState([])
  const [memberOrder, setMemberOrder] = useState([])
  const [dragging, setDragging] = useState(null)    // { axis, id }

  const k = useCallback((suffix, opts) => t(`capacity.activation.${suffix}`, opts), [t])

  const load = useCallback(() => {
    const fetcher = period === 'month' ? getHHMonthlyReport(year, month, costBasis, password)
      : period === 'quarter' ? getHHQuarterlyReport(year, quarterOf(month), costBasis, password)
        : getHHAnnualReport(year, costBasis, password)
    fetcher
      .then(d => { setData(d); setError('') })
      .catch(err => setError(err.response?.data?.detail || k('loadFailed')))
  }, [year, month, period, costBasis, password, k])

  useEffect(() => { load() }, [load])

  // Drag order is per period: a new month can bring different projects and
  // people, and an order carried over from another one would be arbitrary.
  // Seeded once per period -- reloading the same one (an activation toggle,
  // say) must not throw away an order somebody arranged by hand.
  const seededPeriodRef = useRef(null)
  useEffect(() => {
    if (!data) return
    const key = period === 'month' ? `m:${data.year}:${data.month}`
      : period === 'quarter' ? `q:${data.year}:${data.quarter}` : `y:${data.year}`
    if (seededPeriodRef.current === key) return
    seededPeriodRef.current = key
    setProjectOrder(data.projects.map(p => p.project_id))
    setMemberOrder(data.rows.map(r => r.team_member_id))
  }, [data, period])

  useEffect(() => {
    if (!fullscreen) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [fullscreen])

  const shift = (delta) => {
    if (period === 'year') { setYear(y => y + delta); return }
    const step = period === 'month' ? 1 : 3
    const d = new Date(year, month - 1 + delta * step, 1)
    setYear(d.getFullYear())
    setMonth(d.getMonth() + 1)
  }

  const toggleActivation = async (project) => {
    if (period !== 'month') return   // a span activates by month, not as a whole
    setBusyProject(project.project_id)
    try {
      await setHHActivation(project.project_id, year, month, !project.activated)
      load()
    } catch {
      // The checkbox stays as it was; the user can just try again.
    } finally {
      setBusyProject(null)
    }
  }

  const toggleHiddenProject = (id) => setHiddenProjects(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleHiddenMember = (id) => setHiddenMembers(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const onDrop = (axis, toId) => {
    if (!dragging || dragging.axis !== axis || dragging.id === toId) return
    if (axis === 'project') setProjectOrder(o => reordered(o, dragging.id, toId))
    else setMemberOrder(o => reordered(o, dragging.id, toId))
    setDragging(null)
  }
  const dragProps = (axis, id) => ({
    draggable: true,
    onDragStart: () => setDragging({ axis, id }),
    onDragEnd: () => setDragging(null),
    onDragOver: (e) => { if (dragging?.axis === axis) e.preventDefault() },
    onDrop: () => onDrop(axis, id),
  })

  if (error) return <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>
  if (!data) return <p className="text-sm text-gray-400 px-1">{t('capacity.loading')}</p>

  const isMonth = period === 'month'
  const currency = data.cost_currency
  const showMoney = !!data.costs_unlocked
  const projects = byExplicitOrder(data.projects, projectOrder, p => p.project_id)
  const rows = byExplicitOrder(data.rows, memberOrder, r => r.team_member_id)
  const hours = (r, p) => r.by_project[p.project_id] || 0
  // Menus pick the candidates, the table drops what is empty inside that
  // selection, both ways round -- see crossFilter in ./sheet.
  const { rows: visibleRows, projects: visibleProjects } = crossFilter(
    rows.filter(r => !hiddenMembers.has(r.team_member_id)),
    projects.filter(p => !hiddenProjects.has(p.project_id)),
    hours)
  const label = isMonth
    ? new Date(year, month - 1, 1).toLocaleDateString(currentLocale(), {
        month: 'long', year: 'numeric' })
    : period === 'quarter' ? `Q${data.quarter} ${year}` : `${year}`

  // The totals row sums only what is currently visible -- hide a person or a
  // project and the totals shrink to match what is actually on screen.
  const sumRows = (key) => visibleRows.reduce((s, r) => s + (r[key] || 0), 0)
  const sumProject = (projectId) => visibleRows.reduce(
    (s, r) => s + (r.by_project[projectId] || 0), 0)
  const sumCost = () => visibleRows.reduce((s, r) => s + (r.cost_amount || 0), 0)
  const projectCost = (projectId) => visibleRows.reduce((s, r) => {
    const hours = r.by_project[projectId] || 0
    if (!hours || !r.booked || r.cost_amount == null) return s
    // The API prices a person, not a person-project, so their cost is split
    // across the projects their hours landed on. Divided by `booked` rather
    // than `total_hh` on purpose: total_hh also carries vacation and leave,
    // which no project pays for, and only this divisor makes the project
    // costs add back up to the person's own total.
    return s + (r.cost_amount * hours) / r.booked
  }, 0)

  const headCell = 'sticky top-0 z-20 bg-white border-b border-gray-200 px-2 py-2 text-right whitespace-nowrap'
  const footCell = 'px-2 py-2 text-right tabular-nums border-t border-gray-300'

  return (
    <div className={fullscreen
      ? 'fixed inset-0 z-40 bg-gray-50 overflow-auto p-4 space-y-3'
      : 'space-y-4'}>
      {asking && <CostUnlock onClose={() => setAsking(false)} />}
      {drillDown && (
        <WeekDetailModal year={year} month={month} mode={drillDown.mode}
                         id={drillDown.id} name={drillDown.name}
                         onClose={() => setDrillDown(null)} />
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          {['month', 'quarter', 'year'].map(mode => (
            <button key={mode} onClick={() => setPeriod(mode)}
              className={`px-3 py-1.5 ${period === mode ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k(`period_${mode}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">‹</button>
          <span className="text-sm font-semibold text-gray-900 px-2 capitalize min-w-32 text-center">
            {label}
          </span>
          <button onClick={() => shift(1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">›</button>
        </div>

        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          {['company', 'client'].map(basis => (
            <button key={basis} onClick={() => setCostBasis(basis)}
              className={`px-3 py-1.5 ${costBasis === basis ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k(`basis_${basis}`)}
            </button>
          ))}
        </div>

        <VisibilityMenu
          label={k('showProjects')}
          items={projects}
          hidden={hiddenProjects}
          getKey={p => p.project_id}
          getLabel={p => (p.code ? `${p.code} · ${p.name}` : p.name)}
          getHint={p => num(p.hours)}
          onToggle={toggleHiddenProject}
          onShowAll={() => setHiddenProjects(new Set())}
          onHideAll={() => setHiddenProjects(new Set(projects.map(p => p.project_id)))}
          allLabel={k('showAll')}
          noneLabel={k('showNone')}
          emptyLabel={k('noProjects')}
        />
        <VisibilityMenu
          label={k('showPeople')}
          items={rows}
          hidden={hiddenMembers}
          getKey={r => r.team_member_id}
          getLabel={r => r.name}
          getHint={r => num(r.booked)}
          onToggle={toggleHiddenMember}
          onShowAll={() => setHiddenMembers(new Set())}
          onHideAll={() => setHiddenMembers(new Set(rows.map(r => r.team_member_id)))}
          allLabel={k('showAll')}
          noneLabel={k('showNone')}
          emptyLabel={k('noProjects')}
        />

        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setFullscreen(v => !v)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
            {fullscreen ? `⤡ ${k('fullscreenExit')}` : `⤢ ${k('fullscreen')}`}
          </button>
          {unlocked ? (
            <button onClick={lock}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
              🔓 {k('lock')}
            </button>
          ) : (
            <button onClick={() => setAsking(true)}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
              🔒 {k('showCosts')}
            </button>
          )}
          <button
            onClick={() => (isMonth ? exportHHMonthlyReport(year, month, costBasis, password)
              : period === 'quarter'
                ? exportHHQuarterlyReport(year, data.quarter, costBasis, password)
                : exportHHAnnualReport(year, costBasis, password))}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
            {k('export')}
          </button>
        </div>
      </div>

      {!data.costs_unlocked && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          {k('costsLocked')}
        </p>
      )}

      {/* How much of the period has not happened yet. A single total hides it. */}
      {sumRows('forecast') > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {k('forecastNotice', {
            actual: num(sumRows('actual')), forecast: num(sumRows('forecast')),
          })}
        </p>
      )}

      <div className={`bg-white rounded-xl border border-gray-200 overflow-auto ${
        fullscreen ? 'max-h-[calc(100vh-13rem)]' : 'max-h-[65vh]'}`}>
        <table className="w-full text-xs border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-30 bg-white border-b border-gray-200 text-left px-3 py-2 font-semibold text-gray-600">
                {k('person')}
              </th>
              {visibleProjects.map(p => (
                <th key={p.project_id} {...dragProps('project', p.project_id)}
                    className={`${headCell} font-medium text-gray-500 cursor-pointer hover:text-gray-900 ${
                      dragging?.axis === 'project' && dragging.id === p.project_id
                        ? 'opacity-40' : ''}`}
                    title={`${projectTitle(p)} — ${isMonth ? k('openWeekDetail') : k('dragHint')}`}
                    onClick={isMonth
                      ? () => setDrillDown({ mode: 'project', id: p.project_id, name: p.name })
                      : undefined}>
                  <div className="leading-tight">{projectCode(p)}</div>
                  {projectAlias(p) && (
                    <div className="text-[10px] font-normal text-gray-400 leading-tight max-w-24 truncate">
                      {projectAlias(p)}
                    </div>
                  )}
                </th>
              ))}
              <th className={`${headCell} font-medium text-amber-700`}>{k('vacation')}</th>
              <th className={`${headCell} font-medium text-amber-700`}>{k('leave')}</th>
              <th className={`${headCell} font-semibold text-gray-600`}>{k('totalHH')}</th>
              <th className={`${headCell} font-medium text-gray-500`}>{k('hhHabiles')}</th>
              <th className={`${headCell} font-medium text-gray-500`}>{k('diferenciaHH')}</th>
              <th className={`${headCell} font-medium text-red-600`}>{k('overAllocated')}</th>
              <th className={`${headCell} font-medium text-gray-500`}>{k('workTrip')}</th>
              {showMoney && (
                <th className={`${headCell} font-medium text-gray-500`}>
                  {k('cost', { currency })}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => (
              <tr key={row.team_member_id}
                  className={i % 2 ? 'bg-gray-50/60' : 'bg-white'}>
                <td {...dragProps('member', row.team_member_id)}
                    className={`sticky left-0 z-10 bg-inherit px-3 py-1.5 text-gray-800 whitespace-nowrap cursor-pointer hover:text-gray-900 ${
                      dragging?.axis === 'member' && dragging.id === row.team_member_id
                        ? 'opacity-40' : ''}`}
                    title={isMonth ? k('openWeekDetail') : k('dragHint')}
                    onClick={isMonth
                      ? () => setDrillDown({ mode: 'person', id: row.team_member_id, name: row.name })
                      : undefined}>
                  {row.name}
                </td>
                {visibleProjects.map(p => (
                  <td key={p.project_id} className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                    {num(row.by_project[p.project_id])}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                  {num(row.vacation)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                  {num(row.leave)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums font-medium text-gray-900">
                  {num(row.total_hh)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                  {num(row.hh_habiles)}
                </td>
                <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${
                  row.diferencia_hh < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                  {num(row.diferencia_hh)}
                </td>
                <td className={`px-2 py-1.5 text-right tabular-nums ${
                  row.over_allocated > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                  {num(row.over_allocated)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                  {num(row.work_trip)}
                </td>
                {showMoney && (
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                    {money(row.cost_amount, currency)}
                  </td>
                )}
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={1 + visibleProjects.length + 7 + (showMoney ? 1 : 0)}
                    className="px-4 py-8 text-center text-gray-400">
                  {k('noProjects')}
                </td>
              </tr>
            )}
          </tbody>
          {visibleRows.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-gray-900 border-t border-gray-300">
                  {k('totalRow')}
                </td>
                {visibleProjects.map(p => (
                  <td key={p.project_id} className={`${footCell} text-gray-800`}>
                    {num(sumProject(p.project_id))}
                  </td>
                ))}
                <td className={`${footCell} text-gray-800`}>{num(sumRows('vacation'))}</td>
                <td className={`${footCell} text-gray-800`}>{num(sumRows('leave'))}</td>
                <td className={`${footCell} text-gray-900`}>{num(sumRows('total_hh'))}</td>
                <td className={`${footCell} text-gray-800`}>{num(sumRows('hh_habiles'))}</td>
                <td className={`${footCell} ${
                  sumRows('diferencia_hh') < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                  {num(sumRows('diferencia_hh'))}
                </td>
                <td className={`${footCell} ${
                  sumRows('over_allocated') > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {num(sumRows('over_allocated'))}
                </td>
                <td className={`${footCell} text-gray-800`}>{num(sumRows('work_trip'))}</td>
                {showMoney && (
                  <td className={`${footCell} text-gray-800`}>{money(sumCost(), currency)}</td>
                )}
              </tr>
              {showMoney && (
                <tr className="bg-gray-50 text-gray-500">
                  <td className="sticky left-0 z-10 bg-gray-50 px-3 py-1.5">
                    {k('cost', { currency })}
                  </td>
                  {visibleProjects.map(p => (
                    <td key={p.project_id} className="px-2 py-1.5 text-right tabular-nums">
                      {money(projectCost(p.project_id), currency)}
                    </td>
                  ))}
                  <td colSpan={7 + 1} />
                </tr>
              )}
              {isMonth && (
                <tr className="bg-white text-gray-500 border-t border-gray-200">
                  <td className="sticky left-0 z-10 bg-white px-3 py-1.5">
                    {k('activationTitle')}
                  </td>
                  {visibleProjects.map(p => (
                    <td key={p.project_id} className="px-2 py-1.5 text-center">
                      <input type="checkbox" checked={!!p.activated}
                             disabled={busyProject === p.project_id}
                             title={p.activated ? k('activated') : k('pending')}
                             onChange={() => toggleActivation(p)} />
                    </td>
                  ))}
                  <td colSpan={7 + (showMoney ? 1 : 0)} />
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>

      {data.members_missing_rate?.length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {k('missingRates', { names: data.members_missing_rate.join(', ') })}
        </p>
      )}

      {/* PMs and similar roles: nobody plans their hours, but their vacations
          and leave are real and would otherwise vanish from the sheet. */}
      {data.unplanned?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-semibold text-gray-600 mb-1">{k('unplannedTitle')}</h3>
          <p className="text-[11px] text-gray-400 mb-2">{k('unplannedHint')}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
            {data.unplanned.map(u => (
              <span key={u.team_member_id}>
                {u.name}
                {(u.vacation + u.leave) > 0 && (
                  <span className="text-gray-400"> {k('awayHours', { hours: num(u.vacation + u.leave) })}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
